import { useState, useRef, useEffect, useCallback } from "react";
import type { Message } from "~/components/assistant/types";

export interface ScrollManagementOptions {
  messages: Message[];
  isLoadingHistory: boolean;
  currentConversationId: string | null;
}

/**
 * Hook for managing scroll behavior in the assistant.
 *
 * Three scroll behaviors:
 * 1. Loading a non-streaming conversation: instant scroll to bottom
 * 2. Submitting a prompt: smooth scroll to pin user message at top
 * 3. Navigating to a streaming conversation: instant scroll to pin user message at top
 */
export function useScrollManagement({
  messages,
  isLoadingHistory,
  currentConversationId,
}: ScrollManagementOptions) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastScrolledConversationRef = useRef<string | null>(null);
  const shouldSmoothScrollRef = useRef(false);

  // Min-height for agent bubble to pin user message at top during/after streaming
  const [agentMinHeight, setAgentMinHeight] = useState<{ id: string | null; height: number }>({
    id: null,
    height: 0,
  });

  const scrollToBottom = useCallback((behavior: ScrollBehavior | "instant" = "instant") => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: behavior as ScrollBehavior });
    }
  }, []);

  const handleMessageRef = useCallback((messageId: string, el: HTMLDivElement | null) => {
    if (el) {
      messageRefs.current.set(messageId, el);
    } else {
      messageRefs.current.delete(messageId);
    }
  }, []);

  // Reset when conversation changes
  useEffect(() => {
    lastScrolledConversationRef.current = null;
    setAgentMinHeight({ id: null, height: 0 });
  }, [currentConversationId]);

  // Scroll to bottom when loading a non-streaming conversation
  useEffect(() => {
    const hasStreamingAgent = messages.some((m) => m.kind === "model_chat" && m.streaming);

    if (
      currentConversationId &&
      !isLoadingHistory &&
      !hasStreamingAgent &&
      messages.length > 0 &&
      lastScrolledConversationRef.current !== currentConversationId
    ) {
      lastScrolledConversationRef.current = currentConversationId;
      requestAnimationFrame(() => scrollToBottom("instant"));
    }
  }, [currentConversationId, isLoadingHistory, messages, scrollToBottom]);

  // Calculate min-height to pin user message at top during streaming
  useEffect(() => {
    const container = scrollContainerRef.current;
    const contentEl = contentRef.current;
    if (!container || !contentEl || messages.length === 0) {
      return;
    }

    const hasStreamingAgent = messages.some((m) => m.kind === "model_chat" && m.streaming);
    if (!hasStreamingAgent) {
      return;
    }

    const lastUserMessage = [...messages].reverse().find((m) => m.kind === "user_chat");
    if (!lastUserMessage) {
      return;
    }

    const lastUserIndex = messages.indexOf(lastUserMessage);
    const agentMessage = messages.slice(lastUserIndex + 1).find((m) => m.kind === "model_chat");
    if (!agentMessage) {
      return;
    }

    const userElement = messageRefs.current.get(lastUserMessage.id);
    if (!userElement) {
      return;
    }

    const contentStyles = window.getComputedStyle(contentEl);
    const contentPadding =
      parseFloat(contentStyles.paddingTop || "0") + parseFloat(contentStyles.paddingBottom || "0");
    const minHeight = Math.max(
      0,
      Math.floor(
        container.clientHeight - contentPadding - userElement.getBoundingClientRect().height
      )
    );

    setAgentMinHeight({ id: agentMessage.id, height: minHeight });
  }, [messages]);

  // Scroll to pin user message at top:
  // - Smooth scroll when user submits a prompt
  // - Instant scroll when navigating to a streaming conversation
  useEffect(() => {
    if (!agentMinHeight.id) {
      return;
    }

    // User just submitted - smooth scroll
    if (shouldSmoothScrollRef.current) {
      requestAnimationFrame(() => scrollToBottom("smooth"));
      shouldSmoothScrollRef.current = false;
      lastScrolledConversationRef.current = currentConversationId;
      return;
    }

    // Navigated to streaming conversation - instant scroll (once)
    if (lastScrolledConversationRef.current !== currentConversationId) {
      lastScrolledConversationRef.current = currentConversationId;
      requestAnimationFrame(() => scrollToBottom("instant"));
    }
  }, [agentMinHeight, currentConversationId, scrollToBottom]);

  const markUserSubmitted = useCallback(() => {
    shouldSmoothScrollRef.current = true;
  }, []);

  return {
    scrollContainerRef,
    contentRef,
    messageRefs,
    agentMinHeight,
    scrollToBottom,
    handleMessageRef,
    markUserSubmitted,
  };
}
